/**
 * MasterOrdersTab — unified order ledger
 * ---------------------------------------
 * Shows every order (WooCommerce + manual cash/phone/bank) in a single
 * filterable table. Provides:
 *   - KPI cards (Today / 7d / 30d / YTD)
 *   - Filters (source, status, payment method, date range, free-text search)
 *   - Add Manual Order dialog
 *   - Record Refund dialog
 *   - CSV export honouring the current filter set
 *
 * Lives inside CheckOrdersPage as the "Master" tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import {
  Banknote, Phone, ShoppingCart, RefreshCw, Loader2, Plus, Search,
  Download, RotateCcw, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const SOURCE_BADGE = {
  woocommerce: { label: 'WC', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200', icon: ShoppingCart },
  cash: { label: 'Cash', color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200', icon: Banknote },
  phone: { label: 'Phone', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200', icon: Phone },
  bank: { label: 'Bank', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200', icon: Banknote },
  manual: { label: 'Manual', color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200', icon: Banknote },
};

const STATUS_COLORS = {
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/40',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40',
  'on-hold': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40',
  cancelled: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40',
  refunded: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40',
  'partially-refunded': 'bg-orange-100 text-orange-800 dark:bg-orange-900/40',
};

const fmtMoney = (v, currency = 'AUD') => {
  const n = Number(v || 0);
  return `${currency === 'AUD' ? '$' : currency} ${n.toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Australia/Sydney',
    });
  } catch {
    return iso;
  }
};

const authFetch = (url, opts = {}) =>
  fetch(url, { credentials: 'include', ...opts });

const authJsonFetch = (url, body, method = 'POST') =>
  authFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
  });

// ============================================================================

const KpiCard = ({ label, bucket, currency = 'AUD' }) => {
  if (!bucket) return null;
  return (
    <Card data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-bold mt-1">{fmtMoney(bucket.gross, currency)}</div>
        <div className="text-xs text-muted-foreground">
          {bucket.count} orders ·{' '}
          <span className="text-rose-700 dark:text-rose-300">
            {fmtMoney(bucket.refund_total, currency)} refunds
          </span>
        </div>
        <div className="text-xs mt-1 font-semibold">
          Net: {fmtMoney(bucket.net, currency)}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================

const ManualOrderDialog = ({ open, onOpenChange, onCreated }) => {
  const blank = {
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    amount: '',
    source: 'cash',
    payment_method: 'cash',
    items_description: '',
    school_name: '',
    children: [{ name: '', class_name: '' }],
    sales_person: '',
    notes: '',
  };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateChild = (i, key, val) => {
    setForm((f) => {
      const next = [...f.children];
      next[i] = { ...next[i], [key]: val };
      return { ...f, children: next };
    });
  };
  const addChildRow = () =>
    setForm((f) => ({ ...f, children: [...f.children, { name: '', class_name: '' }] }));
  const removeChildRow = (i) =>
    setForm((f) => ({ ...f, children: f.children.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: amt,
        children: form.children.filter((c) => c.name.trim()),
      };
      const res = await authJsonFetch(`${API}/master-orders`, payload, 'POST');
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`Order ${data.order_number} saved`);
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="manual-order-dialog">
        <DialogHeader>
          <DialogTitle>Add Manual Order</DialogTitle>
          <DialogDescription>
            Cash, phone, or bank-transfer order. WC orders flow in automatically daily at 1am Sydney.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <Label>Source *</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="manual-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="manual">Other (manual)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment method *</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="manual-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="eftpos">EFTPOS</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Customer name *</Label>
            <Input data-testid="manual-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          </div>
          <div>
            <Label>Amount (AUD) *</Label>
            <Input data-testid="manual-amount" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div>
            <Label>Customer email</Label>
            <Input data-testid="manual-customer-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
          </div>
          <div>
            <Label>Customer phone</Label>
            <Input data-testid="manual-customer-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
          </div>
          <div>
            <Label>School name</Label>
            <Input data-testid="manual-school" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} />
          </div>
          <div>
            <Label>Sales person</Label>
            <Input data-testid="manual-sales-person" value={form.sales_person} onChange={(e) => setForm({ ...form, sales_person: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Items / description</Label>
            <Textarea rows={2} data-testid="manual-items" value={form.items_description} onChange={(e) => setForm({ ...form, items_description: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Children</Label>
            <div className="space-y-2">
              {form.children.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    placeholder="Child name"
                    value={c.name}
                    onChange={(e) => updateChild(i, 'name', e.target.value)}
                    data-testid={`manual-child-name-${i}`}
                  />
                  <Input
                    placeholder="Class"
                    value={c.class_name || ''}
                    onChange={(e) => updateChild(i, 'class_name', e.target.value)}
                    data-testid={`manual-child-class-${i}`}
                  />
                  {form.children.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeChildRow(i)} data-testid={`manual-child-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addChildRow} data-testid="manual-child-add">
                <Plus className="w-3 h-3 mr-1" /> Add another child
              </Button>
            </div>
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="manual-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="manual-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Save Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================

const RefundDialog = ({ open, onOpenChange, order, onRecorded }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reason, setReason] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && order) {
      const remaining = (Number(order.amount) || 0) - (Number(order.refund_total) || 0);
      setAmount(remaining > 0 ? remaining.toFixed(2) : '');
      setMethod(order.source === 'woocommerce' ? 'woocommerce' : 'cash');
      setReason('');
      setReference('');
    }
  }, [open, order]);

  if (!order) return null;
  const remaining = (Number(order.amount) || 0) - (Number(order.refund_total) || 0);

  const submit = async () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a refund amount > 0');
      return;
    }
    if (amt > remaining + 0.01) {
      toast.error(`Refund cannot exceed remaining ${remaining.toFixed(2)}`);
      return;
    }
    setSaving(true);
    try {
      const res = await authJsonFetch(
        `${API}/master-orders/${order.master_order_id}/refund`,
        { amount: amt, method, reason: reason || null, reference: reference || null },
        'POST',
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`Refund of ${fmtMoney(amt)} recorded (${method})`);
      onRecorded && onRecorded(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(`Refund failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>Record Refund</DialogTitle>
          <DialogDescription>
            Order {order.order_number || order.master_order_id} — {order.customer_name}
            <br />
            Amount {fmtMoney(order.amount)} · already refunded {fmtMoney(order.refund_total || 0)} · remaining{' '}
            <span className="font-semibold">{fmtMoney(remaining)}</span>
            {order.source === 'woocommerce' && (
              <div className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                ℹ️ This is a WooCommerce order — recording a refund here tracks the refund in our system. You must also issue the refund in WordPress (or via your card processor) to actually return the funds to the customer.
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount *</Label>
              <Input data-testid="refund-amount" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label>Method *</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="refund-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="woocommerce">WooCommerce</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Reason</Label>
            <Input data-testid="refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Customer requested / wrong size / etc." />
          </div>
          <div>
            <Label>Reference</Label>
            <Input data-testid="refund-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Cheque #, txn ref, WC refund id" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="refund-save-btn" className="bg-rose-600 hover:bg-rose-700 text-white">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Record Refund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================

export const MasterOrdersTab = () => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    source: 'all',
    status: 'all',
    payment_method: 'all',
    search: '',
    date_from: '',
    date_to: '',
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [refundOrder, setRefundOrder] = useState(null);

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (filters.source && filters.source !== 'all') p.set('source', filters.source);
    if (filters.status && filters.status !== 'all') p.set('status', filters.status);
    if (filters.payment_method && filters.payment_method !== 'all') p.set('payment_method', filters.payment_method);
    if (filters.search) p.set('search', filters.search);
    if (filters.date_from) p.set('date_from', new Date(filters.date_from).toISOString());
    if (filters.date_to) p.set('date_to', new Date(filters.date_to + 'T23:59:59').toISOString());
    return p.toString();
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const [sumRes, listRes] = await Promise.all([
        authFetch(`${API}/master-orders/summary`),
        authFetch(`${API}/master-orders?${qs}&limit=300`),
      ]);
      if (sumRes.ok) setSummary(await sumRes.json());
      const listData = await listRes.json();
      if (!listRes.ok) throw new Error(listData.detail || `HTTP ${listRes.status}`);
      setRows(listData.rows || []);
      setTotal(listData.total || 0);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchEnter = (e) => {
    if (e.key === 'Enter') fetchAll();
  };

  const downloadCsv = () => {
    const qs = buildQuery();
    window.open(`${API}/master-orders/export.csv?${qs}`, '_blank');
  };

  const [resyncDialogOpen, setResyncDialogOpen] = useState(false);
  const [resyncSince, setResyncSince] = useState('2026-01-01');
  const [resyncRunning, setResyncRunning] = useState(false);
  const [resyncJob, setResyncJob] = useState(null); // { job_id, status, ... }

  const triggerWcSync = async () => {
    setLoading(true);
    try {
      const res = await authJsonFetch(`${API}/check-orders/recent-orders/sync`, null, 'POST');
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`Sync complete — ${data.new || 0} new, ${data.updated || 0} updated`);
      await fetchAll();
    } catch (e) {
      toast.error(`Sync failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runResyncSince = async () => {
    if (!resyncSince) {
      toast.error('Pick a "from" date');
      return;
    }
    setResyncRunning(true);
    setResyncJob({ status: 'queued', job_id: null });
    try {
      const res = await authJsonFetch(
        `${API}/master-orders/admin/full-resync`,
        { max_pages: 200, since: resyncSince },
        'POST',
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);

      const jobId = data.job_id;
      setResyncJob({ status: 'queued', job_id: jobId });
      toast.info('Resync started in the background — you can leave this page; results will land in Master Orders when done.');

      // Poll job status every 3s for up to 10 minutes
      const startedAt = Date.now();
      const POLL_MS = 3000;
      const TIMEOUT_MS = 10 * 60 * 1000;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        if (Date.now() - startedAt > TIMEOUT_MS) {
          toast.warning('Resync still running after 10 minutes — check Master Orders later.');
          break;
        }
        try {
          const r = await authFetch(`${API}/master-orders/admin/resync-jobs/${jobId}`);
          if (!r.ok) {
            // Network/transient — keep polling
            continue;
          }
          const job = await r.json();
          setResyncJob(job);
          if (job.status === 'completed') {
            const wc = job.wc_sync || {};
            toast.success(
              `Resync complete (since ${wc.after_date_used || resyncSince}) — ` +
              `fetched ${wc.total_fetched || 0} across ${wc.pages_walked || 0} pages ` +
              `(new ${wc.new || 0}, updated ${wc.updated || 0})`,
              { duration: 12000 },
            );
            setResyncDialogOpen(false);
            await fetchAll();
            break;
          }
          if (job.status === 'failed') {
            toast.error(`Resync failed: ${job.error || 'unknown error'}`, { duration: 12000 });
            break;
          }
          // running / queued → keep polling
        } catch {
          // ignore single poll failure; loop continues
        }
      }
    } catch (e) {
      toast.error(`Resync failed to start: ${e.message}`);
    } finally {
      setResyncRunning(false);
    }
  };

  const sourceCounts = useMemo(() => {
    const c = { woocommerce: 0, cash: 0, phone: 0, bank: 0, manual: 0 };
    rows.forEach((r) => {
      c[r.source] = (c[r.source] || 0) + 1;
    });
    return c;
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="master-orders-tab">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Today" bucket={summary?.today} />
        <KpiCard label="Last 7d" bucket={summary?.last_7d} />
        <KpiCard label="Last 30d" bucket={summary?.last_30d} />
        <KpiCard label="YTD" bucket={summary?.ytd} />
      </div>

      {/* By-source breakdown for YTD */}
      {summary?.by_source_ytd && summary.by_source_ytd.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">YTD by source</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-3 text-sm">
              {summary.by_source_ytd.map((b) => (
                <div key={b.source} className="flex items-center gap-2">
                  <Badge className={SOURCE_BADGE[b.source]?.color || 'bg-slate-100'}>
                    {SOURCE_BADGE[b.source]?.label || b.source}
                  </Badge>
                  <span>{b.count} orders</span>
                  <span className="font-semibold">{fmtMoney(b.gross)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input
                  className="pl-7"
                  placeholder="customer / order# / school / item..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyDown={onSearchEnter}
                  data-testid="master-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
                <SelectTrigger className="w-32" data-testid="master-source-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="woocommerce">WooCommerce</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="w-36" data-testid="master-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="on-hold">On hold</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="partially-refunded">Partial refund</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Payment</Label>
              <Select value={filters.payment_method} onValueChange={(v) => setFilters({ ...filters, payment_method: v })}>
                <SelectTrigger className="w-36" data-testid="master-payment-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="eftpos">EFTPOS</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="woocommerce">WooCommerce</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="afterpay">Afterpay</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" className="w-36" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="master-date-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" className="w-36" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="master-date-to" />
            </div>
            <Button onClick={fetchAll} disabled={loading} data-testid="master-apply-filters">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Apply
            </Button>
            <Button variant="outline" onClick={() => { setFilters({ source: 'all', status: 'all', payment_method: 'all', search: '', date_from: '', date_to: '' }); setTimeout(fetchAll, 0); }} data-testid="master-clear-filters">
              <RotateCcw className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t">
            <div className="text-sm text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{rows.length}</span>{' '}
              of <span className="font-semibold text-foreground">{total}</span> orders
              {total > 300 && <span className="text-xs ml-2">(filter to narrow)</span>}
              <span className="ml-3 text-xs">
                {Object.entries(sourceCounts).filter(([, n]) => n > 0).map(([k, n]) => `${SOURCE_BADGE[k]?.label || k}: ${n}`).join(' · ')}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={triggerWcSync} disabled={loading} data-testid="master-wc-sync-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Sync (last 7d)
              </Button>
              <Button variant="outline" size="sm" onClick={() => setResyncDialogOpen(true)} disabled={loading} data-testid="master-wc-full-resync-btn" className="border-amber-500 text-amber-700 dark:text-amber-300">
                <RefreshCw className="w-4 h-4 mr-1" /> Resync from date…
              </Button>
              <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="master-export-csv-btn">
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
              <Button size="sm" onClick={() => setManualOpen(true)} data-testid="master-add-manual-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Manual Order
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead className="min-w-[280px]">Items / Children</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Refunded</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Sales Person</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                    {loading ? 'Loading…' : 'No orders match the current filters.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => {
                  const SrcIcon = SOURCE_BADGE[r.source]?.icon || ShoppingCart;
                  const remaining = (Number(r.amount) || 0) - (Number(r.refund_total) || 0);
                  const childList = (r.children || []).filter((c) => c && c.name);
                  return (
                    <TableRow key={r.master_order_id} data-testid={`master-row-${r.master_order_id}`}>
                      <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.sale_date)}</TableCell>
                      <TableCell>
                        <Badge className={SOURCE_BADGE[r.source]?.color || 'bg-slate-100'}>
                          <SrcIcon className="w-3 h-3 mr-1" />
                          {SOURCE_BADGE[r.source]?.label || r.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.order_number || '—'}</TableCell>
                      <TableCell className="text-xs max-w-md">
                        <div className="font-medium text-sm leading-snug">
                          {r.items_description || <span className="text-muted-foreground italic">No items recorded</span>}
                        </div>
                        {childList.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {childList.map((c, i) => (
                              <Badge
                                key={`${r.master_order_id}-ch-${i}`}
                                variant="outline"
                                className="text-[10px] font-normal"
                                data-testid={`master-child-${r.master_order_id}-${i}`}
                              >
                                {c.name}{c.class_name ? ` · ${c.class_name}` : ''}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {(r.school_name || r.team_name) && (
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            {r.school_name && <span>School: {r.school_name}</span>}
                            {r.school_name && r.team_name && <span> · </span>}
                            {r.team_name && <span>Team: {r.team_name}</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmtMoney(r.amount, r.currency)}</TableCell>
                      <TableCell className="text-right font-mono">
                        {(Number(r.refund_total) || 0) > 0 ? (
                          <span className="text-rose-700 dark:text-rose-300">{fmtMoney(r.refund_total)}</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[r.status] || 'bg-slate-100'}>{r.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">{r.payment_method_title || r.payment_method || '—'}</TableCell>
                      <TableCell className="text-xs">{r.sales_person || '—'}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={remaining <= 0.001}
                          onClick={() => setRefundOrder(r)}
                          data-testid={`refund-btn-${r.master_order_id}`}
                        >
                          Refund
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ManualOrderDialog open={manualOpen} onOpenChange={setManualOpen} onCreated={fetchAll} />
      <RefundDialog open={!!refundOrder} onOpenChange={(v) => !v && setRefundOrder(null)} order={refundOrder} onRecorded={fetchAll} />

      <Dialog open={resyncDialogOpen} onOpenChange={setResyncDialogOpen}>
        <DialogContent data-testid="resync-dialog">
          <DialogHeader>
            <DialogTitle>Resync WooCommerce</DialogTitle>
            <DialogDescription>
              Pull every WooCommerce order placed on or after the chosen date and mirror them into Master Orders. Anchors before 1 Jan 2026 are clamped to project start. Run this if WP shows more orders than the dashboard.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>From date *</Label>
              <Input
                type="date"
                value={resyncSince}
                onChange={(e) => setResyncSince(e.target.value)}
                min="2026-01-01"
                disabled={resyncRunning}
                data-testid="resync-since-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Default 1 Jan 2026. Use the WC order date you want to start at — older orders already synced will simply be re-confirmed.
              </p>
            </div>
            <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-2">
              Runs in the background — safe to close this dialog. You'll see a toast when it finishes.
            </div>
            {resyncJob && (
              <div className="text-xs border rounded p-2 bg-muted/40 space-y-1" data-testid="resync-job-status">
                <div>
                  Job: <span className="font-mono">{resyncJob.job_id || '—'}</span>{' '}
                  · status:{' '}
                  <span
                    className={
                      resyncJob.status === 'completed' ? 'text-green-700 dark:text-green-300 font-semibold' :
                      resyncJob.status === 'failed' ? 'text-red-700 dark:text-red-300 font-semibold' :
                      'text-amber-700 dark:text-amber-300 font-semibold'
                    }
                  >
                    {resyncJob.status}
                  </span>
                </div>
                {resyncJob.wc_sync && (
                  <div>
                    Pages walked: {resyncJob.wc_sync.pages_walked || 0} · fetched: {resyncJob.wc_sync.total_fetched || 0} · new: {resyncJob.wc_sync.new || 0} · updated: {resyncJob.wc_sync.updated || 0}
                  </div>
                )}
                {resyncJob.error && (
                  <div className="text-red-700 dark:text-red-300 break-words">error: {resyncJob.error}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResyncDialogOpen(false)} disabled={resyncRunning}>Cancel</Button>
            <Button
              onClick={runResyncSince}
              disabled={resyncRunning || !resyncSince}
              data-testid="resync-run-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {resyncRunning && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Run resync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterOrdersTab;
